import { ArticleIcon, CaretDownIcon } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import * as React from 'react';

import {
  type CalendarMomentPrefill,
  articleMomentPrefill,
} from '@/components/reach/editorial/article-promotion';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useArticlePublicationStates } from '@/hooks/reach/useArticleHandoff';
import { useEditorialMap } from '@/hooks/reach/useEditorialMap';
import { ITEM_META } from '@/lib/ui-tokens';

/**
 * "From an article" on the Add-to-the-calendar dialog: the workspace's WRITTEN
 * editorial articles (`/reach/editorial`), searchable by title. Picking one
 * fills the moment's title, its public link and the day it went (or is due to
 * go) live — `articleMomentPrefill` — and the operator can still change any of
 * them before saving.
 *
 * Only articles that exist are offered. A proposed topic has nothing to put on
 * a date yet; it belongs on the editorial programme, not the calendar.
 */
export function MomentArticlePicker({
  onPick,
}: {
  onPick: (prefill: CalendarMomentPrefill) => void;
}): JSX.Element | null {
  const [open, setOpen] = React.useState(false);
  const { data: map, isPending } = useEditorialMap();
  const articles = React.useMemo(
    () => (map?.allTopics ?? []).filter((topic) => topic.hasArticle),
    [map],
  );
  const publications = useArticlePublicationStates(articles.map((article) => article.id));

  // No editorial programme in this workspace: say nothing rather than offer an
  // empty picker on a dialog most workspaces use for filming days and launches.
  if (!isPending && articles.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full justify-between font-normal text-muted-foreground"
          aria-label="Fill from an article"
        >
          <span className="inline-flex items-center gap-2">
            <ArticleIcon weight="regular" />
            From an article
          </span>
          <CaretDownIcon weight="regular" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1.5rem)] p-0"
      >
        <Command>
          <CommandInput placeholder="Search articles…" className="h-9 text-caption" />
          <CommandList>
            <CommandEmpty>{isPending ? 'Loading articles…' : 'No article matches.'}</CommandEmpty>
            <CommandGroup>
              {articles.map((article) => {
                const publication = publications.data?.get(article.id);
                const live = publication?.publishedAt ?? publication?.scheduledAt ?? null;
                return (
                  <CommandItem
                    key={article.id}
                    value={`${article.topic}-${article.id}`}
                    keywords={[article.topic]}
                    onSelect={() => {
                      onPick(
                        articleMomentPrefill({
                          title: article.topic,
                          publishedUrl: publication?.publishedUrl ?? null,
                          publishedAt: publication?.publishedAt ?? null,
                          scheduledAt: publication?.scheduledAt ?? null,
                        }),
                      );
                      setOpen(false);
                    }}
                    className="flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-caption"
                  >
                    <span className="w-full truncate text-foreground">{article.topic}</span>
                    <span className={ITEM_META}>
                      {publication?.state === 'published'
                        ? 'Published'
                        : publication
                          ? 'With the publisher'
                          : 'Written'}
                      {live ? ` · ${format(parseISO(live), 'MMM d, yyyy')}` : ''}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
